import { useEffect, useState } from 'react';
import { Card, Button, Badge, Spinner, FormField } from '../components/ui';

const DOC_TYPES = [
  { value: 'epc',               label: 'EPC (Energy Performance Certificate)', icon: '⚡', required: true },
  { value: 'gas_safety',        label: 'Gas Safety Certificate',               icon: '🔥', required: true },
  { value: 'how_to_rent',       label: 'How to Rent Guide',                    icon: '📖', required: true },
  { value: 'info_sheet',        label: 'Information Sheet (RRA 2025)',          icon: '📋', required: true },
  { value: 'right_to_rent',     label: 'Right to Rent Check',                  icon: '🪪', required: true },
  { value: 'tenancy_agreement', label: 'Tenancy Agreement',                    icon: '✍️', required: true },
  { value: 'other',             label: 'Other Document',                       icon: '📄', required: false },
];

const METHODS = [
  { value: 'email',          label: 'Email' },
  { value: 'in_person',      label: 'In person' },
  { value: 'post',           label: 'Post' },
  { value: 'signed_receipt', label: 'Signed receipt' },
];

const METHOD_ICONS = { email:'✉️', in_person:'🤝', post:'📮', signed_receipt:'✍️' };

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
}

const EMPTY_FORM = {
  tenant_id: '', doc_type: 'epc', served_date: '', method: 'email',
  evidence_url: '', notes: '',
};

export default function DocumentServing() {
  const [docs, setDocs]           = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editID, setEditID]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [filterProp, setFilterProp] = useState('');
  const [filterDoc, setFilterDoc] = useState('');
  const [selectedProp, setSelectedProp] = useState('');
  const [tenants, setTenants]     = useState([]);

  const token = () => localStorage.getItem('token');
  const headers = () => { const ac = localStorage.getItem('lc_access_code'); return { 'Content-Type':'application/json', Authorization:`Bearer ${token()}`, ...(ac ? {'X-Access-Code': ac} : {}) }; };

  useEffect(() => {
    Promise.all([
      fetch('/api/document-serving', { headers: headers() }).then(r => r.json()),
      fetch('/api/properties',       { headers: headers() }).then(r => r.json()),
    ]).then(([d, p]) => {
      setDocs(Array.isArray(d) ? d : []);
      setProperties(Array.isArray(p) ? p : []);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!selectedProp) { setTenants([]); return; }
    fetch(`/api/properties/${selectedProp}/tenants`, { headers: headers() })
      .then(r => r.json()).then(t => setTenants(Array.isArray(t) ? t : []));
  }, [selectedProp]);

  function openNew() {
    setForm(EMPTY_FORM);
    setSelectedProp('');
    setEditID(null);
    setShowForm(true);
  }

  function openEdit(d) {
    setForm({
      tenant_id:    d.tenant_id || '',
      doc_type:     d.doc_type || 'epc',
      served_date:  d.served_date || '',
      method:       d.method || 'email',
      evidence_url: d.evidence_url || '',
      notes:        d.notes || '',
    });
    setSelectedProp(d.property_id);
    setEditID(d.id);
    setShowForm(true);
  }

  async function handleSave() {
    if (!selectedProp) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        tenant_id:    form.tenant_id || null,
        evidence_url: form.evidence_url || null,
        notes:        form.notes || null,
      };

      if (editID) {
        await fetch(`/api/document-serving/${editID}`, { method:'PUT', headers: headers(), body: JSON.stringify(body) });
      } else {
        await fetch(`/api/properties/${selectedProp}/document-serving`, { method:'POST', headers: headers(), body: JSON.stringify(body) });
      }

      const d = await fetch('/api/document-serving', { headers: headers() }).then(r => r.json());
      setDocs(Array.isArray(d) ? d : []);
      setShowForm(false);
    } finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this document record?')) return;
    await fetch(`/api/document-serving/${id}`, { method:'DELETE', headers: headers() });
    setDocs(d => d.filter(x => x.id !== id));
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Filter
  let filtered = docs;
  if (filterProp) filtered = filtered.filter(d => d.property_id === filterProp);
  if (filterDoc)  filtered = filtered.filter(d => d.doc_type === filterDoc);

  // Group by property for summary view
  const byProperty = {};
  properties.forEach(p => { byProperty[p.id] = { property: p, served: {} }; });
  docs.forEach(d => {
    if (byProperty[d.property_id]) {
      if (!byProperty[d.property_id].served[d.doc_type]) {
        byProperty[d.property_id].served[d.doc_type] = d;
      }
    }
  });

  // Count properties missing required docs
  const requiredTypes = DOC_TYPES.filter(d => d.required).map(d => d.value);
  const propsWithGaps = properties.filter(p => {
    const served = byProperty[p.id]?.served || {};
    return requiredTypes.some(t => !served[t]);
  });

  if (loading) return <Spinner />;

  return (
    <div className="fade-up">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:32, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ fontSize:32, fontWeight:300 }}>Document <em>Proof Log</em></h1>
          <p style={{ color:'var(--ink-3)', marginTop:4 }}>Prove when and how you served every legal document — your protection if disputed</p>
        </div>
        <Button onClick={openNew}>+ Log document</Button>
      </div>

      {/* Warning banner */}
      {propsWithGaps.length > 0 && (
        <Card style={{ marginBottom:24, borderColor:'var(--warn)', background:'#fffbeb' }}>
          <p style={{ color:'var(--warn)', fontWeight:600, fontSize:15 }}>
            ⚠️ {propsWithGaps.length} propert{propsWithGaps.length !== 1 ? 'ies' : 'y'} missing required document records
          </p>
          <p style={{ fontSize:13, color:'var(--ink-3)', marginTop:4 }}>
            Missing proof of serving EPC, Gas Safety or How to Rent can invalidate a Section 21 notice. Log every document you've served.
          </p>
        </Card>
      )}

      {/* Property compliance summary */}
      {properties.length > 0 && (
        <div style={{ marginBottom:32 }}>
          <h3 style={{ fontSize:16, marginBottom:16 }}>Document status by property</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {properties.map(p => {
              const served = byProperty[p.id]?.served || {};
              return (
                <Card key={p.id} style={{ padding:'16px 20px' }}>
                  <p style={{ fontWeight:600, marginBottom:10 }}>{p.address_line1}</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {DOC_TYPES.filter(d => d.required).map(dt => {
                      const isServed = !!served[dt.value];
                      return (
                        <div key={dt.value} style={{
                          display:'flex', alignItems:'center', gap:6,
                          padding:'4px 10px', borderRadius:20,
                          background: isServed ? 'var(--success-bg, #f0fdf4)' : '#fef9c3',
                          border: `1px solid ${isServed ? 'var(--success, #16a34a)' : 'var(--warn)'}`,
                          fontSize:12,
                        }}>
                          <span>{dt.icon}</span>
                          <span style={{ color: isServed ? 'var(--success, #16a34a)' : 'var(--warn)' }}>
                            {isServed ? '✓' : '!'} {dt.label.split('(')[0].trim()}
                          </span>
                          {isServed && <span style={{ color:'var(--ink-3)', fontSize:11 }}>{formatDate(served[dt.value].served_date)}</span>}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        {properties.length > 1 && (
          <select value={filterProp} onChange={e => setFilterProp(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--border)', background:'var(--bg)', fontSize:14 }}>
            <option value="">All properties</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.address_line1}</option>)}
          </select>
        )}
        <select value={filterDoc} onChange={e => setFilterDoc(e.target.value)}
          style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--border)', background:'var(--bg)', fontSize:14 }}>
          <option value="">All documents</option>
          {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </div>

      {/* Document log */}
      {filtered.length === 0 ? (
        <Card style={{ textAlign:'center', padding:48 }}>
          <p style={{ fontSize:32, marginBottom:12 }}>📬</p>
          <p style={{ fontWeight:600, marginBottom:8 }}>No documents logged yet</p>
          <p style={{ color:'var(--ink-3)', fontSize:14, marginBottom:20 }}>
            Log every document you serve to tenants — EPC, Gas Safety, How to Rent and more.
            This is your proof if a possession notice is ever challenged.
          </p>
          <Button onClick={openNew}>+ Log your first document</Button>
        </Card>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {filtered.map(d => {
            const docType = DOC_TYPES.find(t => t.value === d.doc_type);
            return (
              <Card key={d.id} style={{ padding:'16px 20px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4, flexWrap:'wrap' }}>
                      <span style={{ fontSize:20 }}>{docType?.icon || '📄'}</span>
                      <p style={{ fontWeight:600, fontSize:15 }}>{d.doc_label}</p>
                      <Badge status="valid" label="Served" />
                    </div>
                    <div style={{ display:'flex', gap:16, flexWrap:'wrap', marginTop:6 }}>
                      <span style={{ fontSize:13, color:'var(--ink-3)' }}>🏘 {d.property_address}</span>
                      {d.tenant_name && <span style={{ fontSize:13, color:'var(--ink-3)' }}>👤 {d.tenant_name}</span>}
                      <span style={{ fontSize:13, color:'var(--ink-3)' }}>📅 {formatDate(d.served_date)}</span>
                      <span style={{ fontSize:13, color:'var(--ink-3)' }}>{METHOD_ICONS[d.method]} {d.method_label}</span>
                    </div>
                    {d.evidence_url && (
                      <a href={d.evidence_url} target="_blank" rel="noreferrer"
                        style={{ fontSize:13, color:'var(--accent)', marginTop:4, display:'block' }}>
                        📎 View evidence
                      </a>
                    )}
                    {d.notes && <p style={{ fontSize:13, color:'var(--ink-3)', marginTop:4, fontStyle:'italic' }}>{d.notes}</p>}
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(d)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id)} style={{ color:'var(--danger)' }}>Delete</Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, backdropFilter:'blur(2px)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 20px', overflowY:'auto' }}
          onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:'100%', maxWidth:520, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
            <h3 style={{ fontSize:20, marginBottom:20 }}>{editID ? 'Edit document record' : 'Log document served'}</h3>

            <FormField label="Property *">
              <select value={selectedProp} onChange={e => { setSelectedProp(e.target.value); setForm(f => ({ ...f, tenant_id:'' })); }} disabled={!!editID}>
                <option value="">Select property…</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.address_line1}</option>)}
              </select>
            </FormField>

            {selectedProp && tenants.length > 0 && (
              <FormField label="Tenant">
                <select value={form.tenant_id} onChange={set('tenant_id')}>
                  <option value="">Select tenant (optional)…</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select>
              </FormField>
            )}

            <FormField label="Document type *">
              <select value={form.doc_type} onChange={set('doc_type')}>
                {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.icon} {d.label}</option>)}
              </select>
            </FormField>

            <FormField label="Date served *">
              <input type="date" value={form.served_date} onChange={set('served_date')} />
            </FormField>

            <FormField label="How was it served? *">
              <select value={form.method} onChange={set('method')}>
                {METHODS.map(m => <option key={m.value} value={m.value}>{METHOD_ICONS[m.value]} {m.label}</option>)}
              </select>
            </FormField>

            <FormField label="Evidence URL (optional)">
              <input value={form.evidence_url} onChange={set('evidence_url')} placeholder="Link to email, scan, or upload…" />
            </FormField>

            <FormField label="Notes (optional)">
              <textarea value={form.notes} onChange={set('notes')} rows={2} placeholder="e.g. Sent via email, acknowledged by tenant…" />
            </FormField>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button loading={saving} onClick={handleSave} disabled={!selectedProp || !form.served_date}>
                {editID ? 'Save changes' : 'Log document'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
